import { createContext, FC, ReactElement, useCallback, useContext, useRef, useState } from 'react';
import { Drawer, DrawerProps } from '@silverfort/sf-components';

type OpenDrawerParams = Omit<DrawerProps, 'open' | 'children'> & {
    DrawerContent: ReactElement;
    DrawerContentPlaceholder?: ReactElement;
};

interface DrawerContextValue {
    openDrawer: (drawerProps: OpenDrawerParams) => void;
    closeDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextValue | undefined>(undefined);

export const DrawerProvider: FC = ({ children }) => {
    const [drawerProps, setDrawerProps] = useState<DrawerProps>({ open: false });
    const drawerContentRef = useRef<ReactElement | null>(null);

    const openDrawer: DrawerContextValue['openDrawer'] = useCallback((params) => {
        const { DrawerContent, DrawerContentPlaceholder, ...props } = params;

        setDrawerProps({ ...props, open: true, children: DrawerContentPlaceholder ?? DrawerContent });
        drawerContentRef.current = DrawerContent;
    }, []);

    const closeDrawer: DrawerContextValue['closeDrawer'] = useCallback(() => {
        setDrawerProps((props) => ({ ...props, open: false }));
        drawerContentRef.current = null;
    }, []);

    const contextValue: DrawerContextValue = { openDrawer, closeDrawer };

    return (
        <DrawerContext.Provider value={contextValue}>
            {children}
            <Drawer
                {...drawerProps}
                onClose={closeDrawer}
                onTransitionExited={() => {
                    setDrawerProps((props) => ({ ...props, children: null }));
                }}
                onTransitionEnter={() => {
                    setDrawerProps((props) => ({ ...props, children: drawerContentRef.current }));
                }}
            />
        </DrawerContext.Provider>
    );
};

export const useDrawer = () => {
    const context = useContext(DrawerContext);

    if (!context) {
        throw new Error('useDrawer must be used within a DrawerProvider');
    }

    return context;
};
